import { useMemo } from 'react'

import {
  CustomXAxisTick,
  CustomYAxisTick,
} from 'features/charts-v2/utils/AxisTickUtils'

interface UseXAxisTickRendererOptions {
  fontSizePx: number
  overflowMode: 'expand' | 'wrap' | 'truncate'
  maxWidth: number
}

interface UseYAxisTickRendererOptions {
  fontSizePx: number
  overflowMode: 'expand' | 'wrap' | 'truncate'
  truncateAtChars?: number
  wrapAtPx?: number
  /** Label thinning factor from `useYAxisLabelStep`, forwarded so thinned
   *  labels fade instead of Recharts mounting/unmounting them. */
  step?: number
}

interface RechartsXTickProps {
  x: number
  y: number
  payload: { value: string }
  [key: string]: unknown
}

interface RechartsYTickProps {
  x: number
  y: number
  payload: { value: string }
  [key: string]: unknown
}

export const useXAxisTickRenderer = ({
  fontSizePx,
  overflowMode,
  maxWidth,
}: UseXAxisTickRendererOptions): ((
  props: RechartsXTickProps,
) => JSX.Element) => {
  return useMemo(() => {
    const XAxisTickRenderer = (props: RechartsXTickProps): JSX.Element => (
      <CustomXAxisTick
        {...props}
        fontSizePx={fontSizePx}
        overflowMode={overflowMode}
        maxWidth={maxWidth}
      />
    )
    XAxisTickRenderer.displayName = 'XAxisTickRenderer'
    return XAxisTickRenderer
  }, [fontSizePx, overflowMode, maxWidth])
}

export const useYAxisTickRenderer = ({
  fontSizePx,
  overflowMode,
  truncateAtChars,
  wrapAtPx,
  step,
}: UseYAxisTickRendererOptions): ((
  props: RechartsYTickProps,
) => JSX.Element) => {
  return useMemo(() => {
    const YAxisTickRenderer = (props: RechartsYTickProps): JSX.Element => (
      <CustomYAxisTick
        {...props}
        fontSizePx={fontSizePx}
        overflowMode={overflowMode}
        truncateAtChars={truncateAtChars}
        wrapAtPx={wrapAtPx}
        step={step}
      />
    )
    YAxisTickRenderer.displayName = 'YAxisTickRenderer'
    return YAxisTickRenderer
  }, [fontSizePx, overflowMode, truncateAtChars, wrapAtPx, step])
}
